import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  MoreHorizontal, 
  Search, 
  Filter, 
  Trash2, 
  Edit, 
  Star, 
  AlertTriangle,
  Calendar,
  User,
  Plus
} from 'lucide-react';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { 
  useBehaviorRecords, 
  useDeleteBehaviorRecord, 
  useUpdateBehaviorRecord, 
  BehaviorRecord 
} from '@/hooks/useBehaviorTracking';
import { useLanguage } from '@/contexts/LanguageContext';

interface BehaviorRecordsListProps {
  guardId?: string;
  teamId?: string;
  showGuardColumn?: boolean;
  canAddBehavior?: boolean;
  onAddBehavior?: () => void;
}

export const BehaviorRecordsList: React.FC<BehaviorRecordsListProps> = ({
  guardId,
  teamId,
  showGuardColumn = true,
  canAddBehavior = false,
  onAddBehavior,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'positive' | 'negative'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'reversed' | 'disputed'>('all');
  const [deleteRecord, setDeleteRecord] = useState<BehaviorRecord | null>(null);
  const { language } = useLanguage();

  const getLocalizedName = (record: BehaviorRecord) => {
    const type = record.behavior_type;
    if (!type) return '';
    if (language === 'el') return type.name_el || type.name;
    if (language === 'en') return type.name_en || type.name;
    if (language === 'fr') return type.name_fr || type.name;
    return type.name;
  };

  const getLocalizedDescription = (record: BehaviorRecord) => {
    const type = record.behavior_type;
    if (!type) return '';
    if (language === 'el') return type.description_el || type.description;
    if (language === 'en') return type.description_en || type.description;
    if (language === 'fr') return type.description_fr || type.description;
    return type.description;
  };

  const { data: records = [], isLoading } = useBehaviorRecords({
    guardId,
    teamId,
    limit: 50,
  });
  
  const { mutate: updateRecord } = useUpdateBehaviorRecord();
  const { mutate: deleteRecordMutation } = useDeleteBehaviorRecord();

  const filteredRecords = records.filter((record) => {
    const matchesSearch = !searchTerm || 
      record.behavior_type?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.guard?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${record.guard?.first_name} ${record.guard?.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = categoryFilter === 'all' || 
      record.behavior_type?.category === categoryFilter;

    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleStatusChange = (record: BehaviorRecord, newStatus: 'active' | 'reversed' | 'disputed') => {
    updateRecord({
      recordId: record.id,
      updates: { status: newStatus },
    });
  };

  const handleDelete = () => {
    if (deleteRecord) {
      deleteRecordMutation(deleteRecord.id);
      setDeleteRecord(null);
    }
  };

  const getBehaviorIcon = (record: BehaviorRecord) => {
    if (record.behavior_type?.icon) return record.behavior_type.icon;
    return record.behavior_type?.category === 'positive' ? '⭐' : '⚠️';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'reversed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'disputed':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'positive':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'negative':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ιστορικό Συμπεριφορών</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Ιστορικό Συμπεριφορών
              </CardTitle>
              <CardDescription>
                Καταγεγραμμένες συμπεριφορές και η επίδρασή τους στην αξιολόγηση
              </CardDescription>
            </div>
            {canAddBehavior && onAddBehavior && (
              <Button onClick={onAddBehavior} className="gap-2">
                <Plus className="h-4 w-4" />
                Καταχώρηση Συμπεριφοράς
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Αναζήτηση συμπεριφορών..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={categoryFilter} onValueChange={(value: any) => setCategoryFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλες οι κατηγορίες</SelectItem>
                <SelectItem value="positive">Θετικές</SelectItem>
                <SelectItem value="negative">Αρνητικές</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλες</SelectItem>
                <SelectItem value="active">Ενεργές</SelectItem>
                <SelectItem value="reversed">Ακυρωμένες</SelectItem>
                <SelectItem value="disputed">Αμφισβητούμενες</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Records List */}
          {filteredRecords.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Δεν βρέθηκαν καταγραφές συμπεριφορών</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRecords.map((record) => (
                <div
                  key={record.id}
                  className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-4 flex-1">
                    {/* Behavior Icon */}
                    <div className="text-2xl mt-1">
                      {getBehaviorIcon(record)}
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-lg">
                            {getLocalizedName(record)}
                          </h4>
                          {getLocalizedDescription(record) && (
                            <p className="text-sm text-gray-600 mt-1">
                              {getLocalizedDescription(record)}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge
                            className={getCategoryColor(record.behavior_type?.category)}
                          >
                            {record.behavior_type?.category === 'positive' ? (
                              <Star className="h-3 w-3 mr-1" />
                            ) : (
                              <AlertTriangle className="h-3 w-3 mr-1" />
                            )}
                            {record.behavior_type?.category === 'positive' ? 'Θετική' : 'Αρνητική'}
                          </Badge>
                          <Badge
                            variant={(record.behavior_type?.score_impact || 0) > 0 ? 'default' : 'destructive'}
                            className="text-xs"
                          >
                            {(record.behavior_type?.score_impact || 0) > 0 ? '+' : ''}{record.behavior_type?.score_impact || 0} πόντοι
                          </Badge>
                        </div>
                      </div>

                      {/* Guard and Date Info */}
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {showGuardColumn && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span>
                              {record.guard?.full_name || 
                               `${record.guard?.first_name} ${record.guard?.last_name}`}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {format(new Date(record.incident_date), 'PPP', { locale: el })}
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      {record.description && (
                        <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                          {record.description}
                        </p>
                      )}

                      {/* Status and Recorded By */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(record.status)}>
                            {record.status === 'active' ? 'Ενεργή' :
                             record.status === 'reversed' ? 'Ακυρωμένη' : 'Αμφισβητούμενη'}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-500">
                          Καταγράφηκε από: {record.recorded_by_profile?.full_name || 
                            `${record.recorded_by_profile?.first_name} ${record.recorded_by_profile?.last_name}`}
                        </div>
                      </div>
                    </div>

                    {/* Actions Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {record.status === 'active' && (
                          <DropdownMenuItem
                            onClick={() => handleStatusChange(record, 'reversed')}
                          >
                            Ακύρωση
                          </DropdownMenuItem>
                        )}
                        {record.status === 'reversed' && (
                          <DropdownMenuItem
                            onClick={() => handleStatusChange(record, 'active')}
                          >
                            Επαναφορά
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleStatusChange(record, 'disputed')}
                          disabled={record.status === 'disputed'}
                        >
                          Αμφισβήτηση
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setDeleteRecord(record)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Διαγραφή
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteRecord} onOpenChange={() => setDeleteRecord(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Επιβεβαίωση Διαγραφής</AlertDialogTitle>
            <AlertDialogDescription>
              Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την καταγραφή συμπεριφοράς; 
              Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};